import React from 'react';
import Styled from 'styled-components';
import { Flex } from '@ui-library/misc';
import Svg from '@svg/index';
import SourceIcon from '@components/common/icons/sourceIcon';
import DestinationIcon from '@components/common/icons/destinationIcon';
import { Select, Dropdown, Menu } from 'antd';
import StartSyncButton from '../StartSyncButton';
import { StopOutlined, SyncOutlined, DownOutlined } from '@ant-design/icons';
import { IDestination } from '@stores/destination';
import NextSchedule from '@components/sources/sourceDetails/cloudSourceDetails/cloudSourceReports/NextSchedule';
import { useCloudSourceInfo } from '@stores/cloudSourcesInfo/cloudSourcesInfo';
import Button from '@ui-library/button';
import useStores from '@stores/useStores';
import { syncBehaviourLabels } from '@components/sources/visualizer/mappingScreens/config/syncBehaviourConfig';
import { FixMe } from '@utils/types';
import classes from './sync-report-common.module.scss';
import DocLinkLabel from '../../../docLinksLabel';
import { WAREHOUSE_DOC_LINKS } from '../../../source/warehouseSource/warehouseSourceList/warehouseSettings';
import InfoPopover from '@ui-library/infoPopover';
import SyncBehaviourModeFactory from './syncBehaviourModeFactory';

const { Option } = Select;

const SelectDestination = Styled(Select)`
.ant-select-selector{
  border:1px solid transparent!important;
  &:hover{
    border:1px solid #d9d9d9!important;
  }
}
`;
export interface DestinationSelectProps {
  isAdmin: boolean;
  sourceName: string;
  sourceRole: string;
  defaultDestinationId: string;
  destinations: Pick<IDestination, 'id' | 'destinationDefinition' | 'name'>[];
  onChange: (destinationId: string) => void;
  nextSyncAt?: string;
}
const SourceContainer = Styled.div`
display: flex;
flex-direction: row;
justify-content: center;
align-items: center;
margin: 0 8px 0 0;
width: 24px;
height: 24px;
border: 2px solid #E0E0E0;
border-radius: 24px;
img {
  max-height: 32px;
  max-width: 32px;
}
`;

const SelectContainer = Styled(Flex)`
  border:1px solid #E0E0E0;
  border-radius:4px;
  padding:4px;
  background-color:#ffffff;
`;

export const supportIncremental = (
  sourceDefName?: string,
  mode?: string,
  enableIncremental?: boolean,
) => !!mode || (enableIncremental && sourceDefName?.toLowerCase() === 's3');

const DestinationSelect: React.FC<DestinationSelectProps> = ({
  isAdmin,
  sourceName,
  sourceRole,
  defaultDestinationId,
  destinations,
  onChange,
  nextSyncAt,
}) => {
  const { source } = useCloudSourceInfo();
  const { workspaceStore } = useStores();
  const selectedDestination = destinations.find((d) => d.id === defaultDestinationId);
  if (!selectedDestination) {
    return null;
  }
  const showIncrementalButton = supportIncremental(
    source.sourceDef?.name,
    source?.config?.syncBehaviour?.mode,
    workspaceStore.whActionsSyncBehaviours.length > 0,
  );

  const { docLinkDescription } = SyncBehaviourModeFactory(source?.config?.syncBehaviour?.mode);
  return (
    <Flex alignItems="center">
      <SelectContainer alignItems="center">
        <SourceContainer style={{ marginRight: 4 }}>
          <SourceIcon size={16} source={sourceRole} />
        </SourceContainer>
        <div style={{ marginRight: 4 }}>{sourceName}</div>
        <Svg name="right-arrow" style={{ marginRight: 4 }} fill="black" />

        <SelectDestination
          onChange={(v) => onChange(v as string)}
          showArrow={false}
          defaultValue={selectedDestination.id}
          style={{ marginTop: 1 }}
        >
          {destinations.map((d) => (
            <Option value={d.id!}>
              <Flex alignItems="center">
                <SourceContainer style={{ marginRight: 4 }}>
                  <DestinationIcon
                    width={16}
                    height={16}
                    destination={d.destinationDefinition.name}
                  />
                </SourceContainer>
                {d.name}
              </Flex>
            </Option>
          ))}
        </SelectDestination>
      </SelectContainer>
      <div className={classes.sync_type_container}>
        {syncBehaviourLabels[source.config?.syncBehaviour?.mode || ''] && (
          <div className={classes.sync_type}>
            Sync mode: {syncBehaviourLabels[source.config?.syncBehaviour?.mode || '']}
            {docLinkDescription && (
              <InfoPopover
                content={
                  <DocLinkLabel
                    description={`
                ${docLinkDescription}
                For more info on ${
                  syncBehaviourLabels[source.config?.syncBehaviour?.mode || '']
                } and other modes visit`}
                    linkRef={WAREHOUSE_DOC_LINKS.syncModes}
                    linkText="Sync Modes"
                    insidePopover
                  />
                }
                size="l"
                iconFontSize="16px"
              />
            )}
          </div>
        )}
        <NextSchedule nextSchedule={nextSyncAt} isAdmin={isAdmin} />
      </div>

      <div style={{ marginLeft: 'auto' }}>
        <StartSyncButton
          isAdmin={isAdmin}
          buttonComponent={(props: FixMe) => {
            const syncMenu = (
              <Menu onClick={props.runFromDropDown}>
                <Menu.Item key="full">Force Full Resync</Menu.Item>
              </Menu>
            );

            return showIncrementalButton ? (
              <Dropdown.Button
                icon={<DownOutlined />}
                disabled={props?.syncing || props?.disabled}
                size="large"
                trigger={['click']}
                buttonsRender={(buttons) => {
                  buttons[0] = (
                    <Button
                      secondary
                      size="large"
                      icon={props.syncing ? <SyncOutlined spin /> : <SyncOutlined />}
                      {...props}
                      style={{}}
                    />
                  );
                  return buttons;
                }}
                overlay={syncMenu}
              />
            ) : (
              <Button
                secondary
                icon={props.syncing ? <StopOutlined /> : <SyncOutlined />}
                {...props}
              />
            );
          }}
        />
      </div>
    </Flex>
  );
};

export default DestinationSelect;
